import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Box, CircularProgress, Stack} from '@mui/material';
import {Body, Button, Icon} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {ENTER_KEY} from '@/config/constants';
import RadialMenu, {RadialMenuItem} from '@/components/radial-menu';
import type {PendingAttachment} from '@/hooks/use-message-attachments/useMessageAttachments';
import styles from './ChatInput.module.scss';

const EMOJI_OPTIONS = ['👍', '👀', '🚩', '✅', '🔥', '😊'] as const;
const INITIAL_ANGLE = 200;
const ANGLE_OFFSET = 35;
// 1 line + vertical padding; matches the send button once the user has typed.
const DEFAULT_HEIGHT_PX = 32;
// Empty: 2 rows so a wrapping placeholder isn't clipped. Padding 6+6 + 20*2.
const EMPTY_PLACEHOLDER_HEIGHT_PX = 52;
const MAX_HEIGHT_PX = 320;
const KEYBOARD_RESIZE_STEP_PX = 8;

function clampChatInputHeight(height: number) {
  return Math.min(
    MAX_HEIGHT_PX,
    Math.max(DEFAULT_HEIGHT_PX, Math.round(height))
  );
}

const EMOJI_ITEMS: RadialMenuItem[] = EMOJI_OPTIONS.map((emoji, index) => ({
  id: emoji,
  angle: INITIAL_ANGLE + index * ANGLE_OFFSET,
  content: <span style={{fontSize: '20px', lineHeight: 1}}>{emoji}</span>,
}));

export type ChatInputAttachmentsConfig = {
  pending: PendingAttachment[];
  isUploading: boolean;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveAttachment: (sourceId: string) => void;
  accept: string;
};

export type ChatInputProps = {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder?: string;
  disabled?: boolean;
  isSubmitting?: boolean;
  showEmoji?: boolean;
  attachments?: ChatInputAttachmentsConfig;
  autoFocus?: boolean;
};

function ChatInput({
  value,
  onChange,
  onSubmit,
  placeholder = translate('Add a comment...'),
  disabled = false,
  isSubmitting = false,
  showEmoji = false,
  attachments,
  autoFocus = false,
}: ChatInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dragCleanupRef = useRef<(() => void) | null>(null);
  const dragStartYRef = useRef(0);
  const dragStartHeightRef = useRef(0);
  const [minHeightPx, setMinHeightPx] = useState(DEFAULT_HEIGHT_PX);
  const [renderedHeightPx, setRenderedHeightPx] = useState(DEFAULT_HEIGHT_PX);
  const minHeightPxRef = useRef(minHeightPx);
  minHeightPxRef.current = minHeightPx;

  const applyTextareaHeightRef = useRef(() => {});
  applyTextareaHeightRef.current = () => {
    const el = textareaRef.current;
    if (!el) {
      return;
    }
    // Collapse first so shrinking a previous drag/content size measures correctly.
    el.style.height = '0px';
    const emptyFloor = value ? DEFAULT_HEIGHT_PX : EMPTY_PLACEHOLDER_HEIGHT_PX;
    const nextHeight = clampChatInputHeight(
      Math.max(minHeightPxRef.current, emptyFloor, el.scrollHeight)
    );
    el.style.height = `${nextHeight}px`;
    setRenderedHeightPx((current) =>
      current === nextHeight ? current : nextHeight
    );
  };

  useLayoutEffect(() => {
    applyTextareaHeightRef.current();
  }, [value, minHeightPx]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) {
      return undefined;
    }
    let {width: lastWidth} = el.getBoundingClientRect();
    const observer = new ResizeObserver(() => {
      const {width} = el.getBoundingClientRect();
      if (width === lastWidth) {
        return;
      }
      lastWidth = width;
      applyTextareaHeightRef.current();
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    return () => {
      dragCleanupRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (autoFocus) {
      textareaRef.current?.focus();
    }
  }, [autoFocus]);

  const isBlocked =
    disabled || isSubmitting || (attachments?.isUploading ?? false);
  const canSend = value.trim() !== '' && !isBlocked;

  const hasPendingAttachments =
    (attachments?.isUploading ?? false) ||
    (attachments?.pending.length ?? 0) > 0;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== ENTER_KEY.key || e.nativeEvent.isComposing) {
      return;
    }
    if (!(e.metaKey || e.ctrlKey)) {
      return;
    }
    e.preventDefault();
    if (!isBlocked && canSend) {
      onSubmit();
    }
  };

  const handleEmojiSelect = (emoji: string) => {
    onChange(value + emoji);
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleResizePointerDown = (event: React.PointerEvent<HTMLElement>) => {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.focus();

    dragCleanupRef.current?.();

    const measured = textareaRef.current?.offsetHeight ?? 0;
    dragStartYRef.current = event.clientY;
    dragStartHeightRef.current = measured > 0 ? measured : minHeightPx;

    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ns-resize';

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const nextHeight = clampChatInputHeight(
        dragStartHeightRef.current + (dragStartYRef.current - moveEvent.clientY)
      );
      const el = textareaRef.current;
      if (el) {
        el.style.height = `${nextHeight}px`;
      }
      setMinHeightPx(nextHeight);
    };

    const endDrag = () => {
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', endDrag);
      document.removeEventListener('pointercancel', endDrag);
      dragCleanupRef.current = null;
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', endDrag);
    document.addEventListener('pointercancel', endDrag);
    dragCleanupRef.current = endDrag;
  };

  const handleResizeKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
      return;
    }
    event.preventDefault();
    const measured = textareaRef.current?.offsetHeight ?? minHeightPx;
    const delta =
      event.key === 'ArrowUp'
        ? KEYBOARD_RESIZE_STEP_PX
        : -KEYBOARD_RESIZE_STEP_PX;
    const nextHeight = clampChatInputHeight(measured + delta);
    const el = textareaRef.current;
    if (el) {
      el.style.height = `${nextHeight}px`;
    }
    setMinHeightPx(nextHeight);
  };

  return (
    <Stack gap="4px">
      <Stack className={styles.inputRow}>
        {/* APG window splitter: a focusable separator with arrow-key resizing. */}
        {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        <Box
          className={styles.resizeHandle}
          onPointerDown={handleResizePointerDown}
          onKeyDown={handleResizeKeyDown}
          role="separator"
          tabIndex={0}
          aria-orientation="horizontal"
          aria-label={translate('Resize message input')}
          aria-valuemin={DEFAULT_HEIGHT_PX}
          aria-valuemax={MAX_HEIGHT_PX}
          aria-valuenow={renderedHeightPx}
        />
        {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        <Stack direction="row" alignItems="flex-end" gap="4px">
          <textarea
            ref={textareaRef}
            className={styles.textarea}
            placeholder={placeholder}
            value={value}
            rows={1}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            data-min-height={minHeightPx}
            disabled={disabled || isSubmitting}
          />
          {showEmoji ? (
            <RadialMenu
              interactionMode="click"
              items={EMOJI_ITEMS}
              selectedIds={[]}
              radius={60}
              offsetRotation={-45}
              onOptionClick={handleEmojiSelect}
              trigger={
                <Button
                  size="small"
                  appearance="neutral"
                  mode="plain"
                  className={styles.emojiButton}
                  aria-label={translate('Add emoji')}
                  disabled={isBlocked}
                >
                  😊
                </Button>
              }
            />
          ) : null}
          {attachments ? (
            <Button
              size="small"
              appearance="neutral"
              mode="plain"
              className={styles.attachButton}
              onClick={handleAttachClick}
              disabled={isBlocked}
              aria-label={translate('Attach file')}
            >
              <Icon
                size="small"
                appearance="neutral"
                name="Navigation-Attach"
              />
            </Button>
          ) : null}
          <Button
            size="small"
            appearance="neutral"
            mode="filled"
            className={styles.sendButton}
            onClick={onSubmit}
            disabled={!canSend}
            aria-label={translate('Send')}
          >
            <Icon name="Navigation-Share" size="small" appearance="white" />
          </Button>
        </Stack>
      </Stack>
      {hasPendingAttachments && attachments ? (
        <Stack gap="4px" className={styles.pendingAttachments}>
          {attachments.isUploading ? (
            <Stack direction="row" alignItems="center" gap="6px">
              <CircularProgress size={12} />
              <Body size="xs">{translate('Uploading...')}</Body>
            </Stack>
          ) : null}
          {attachments.pending.map((attachment) => (
            <Stack
              key={attachment.sourceId}
              direction="row"
              alignItems="center"
              gap="6px"
            >
              <Body size="xs">{attachment.fileName}</Body>
              <Button
                size="small"
                appearance="neutral"
                mode="plain"
                className={styles.removeAttachmentButton}
                onClick={() =>
                  attachments.onRemoveAttachment(attachment.sourceId)
                }
                aria-label={translate('Remove attachment')}
              >
                <Icon
                  size="small"
                  appearance="neutral"
                  name="Actions-Close-Normal"
                />
              </Button>
            </Stack>
          ))}
        </Stack>
      ) : null}
      {attachments ? (
        <input
          ref={fileInputRef}
          type="file"
          accept={attachments.accept}
          multiple
          onChange={attachments.onFileChange}
          className={styles.hiddenInput}
        />
      ) : null}
    </Stack>
  );
}

export default ChatInput;
